import type { ApolloClient } from '@apollo/client';
import * as Sentry from '@sentry/react';

import { LOCATE_PK } from '../gql/queries/geoPositions/geoPositionRoads';
import { getSeedKey } from '../gql/cacheSeed';
import {
  getCachedRoadMeshEntries,
  hydrateRoadMeshes,
} from '../offline/roadMeshStore';
import { traceAutolocate } from './autolocateTrace';
import { withTimeout, NetworkTimeoutError } from './withTimeout';
import {
  pkCandidatesFromMeshEntries,
  pickRoadWithPreference,
  AMBIGUITY_MARGIN_METERS,
  PK_MATCH_MAX_METERS,
} from './pkFromMesh';
import { pkWithinRange } from './roadStretches';
import type { RoadPkRange } from './pkValidation';

/**
 * «¿En qué vía estoy y con qué PK?», para el botón de autolocalizar PK de
 * Actuación y Accidente.
 *
 * ── Qué NO reinventa ───────────────────────────────────────────────────────
 *
 * La regla de elección de carretera es la del recorrido: `pkRoadWithPreference`
 * con el margen de 50 m, medido sobre el tramo que la C-25 y su ramal comparten
 * de verdad. Dos criterios distintos para elegir vía acabarían discrepando
 * justo ahí, que es donde importa.
 *
 * ── Qué añade ──────────────────────────────────────────────────────────────
 *
 * Dos cosas que el recorrido no necesita y el botón sí:
 *
 *  1. **Decir por qué no.** `pkFromMeshes` devuelve `null` más allá del umbral
 *     y ahí se acaba: al operario del recorrido le vale, porque teclea el PK.
 *     Aquí hay que poder decirle «no estás sobre ninguna vía; la más cercana es
 *     la C-25, a 480 m», o el botón parece roto.
 *  2. **Comprobar que el PK cabe en la vía.** La malla puede llegar más lejos
 *     que el rango declarado en `Road` (pasó con la C-25 hasta que se alineó, y
 *     sigue pasando con las vías cartografiadas de más). Rellenar ahí pondría
 *     un PK que el propio backend rechaza al guardar.
 */
export type PkCandidate = {
  roadId: string;
  roadName: string;
  direction: string;
  pkKm: number;
  pkMeter: number;
  /** Distancia perpendicular a la vía, en metros. */
  distance: number;
  /** Extremos declarados. Ausente = no se conocen; no bloquea. */
  roadRange?: RoadPkRange;
};

export type PkResolution =
  | {
      kind: 'ok';
      match: PkCandidate;
      /** La vía resultante NO es la que traía el formulario. */
      changedRoad: boolean;
      /** Otra vía a menos del margen de ambigüedad: zona de solape. */
      ambiguousWith?: PkCandidate;
    }
  | { kind: 'out-of-range'; match: PkCandidate }
  | { kind: 'too-far'; nearest: PkCandidate }
  | { kind: 'no-data' };

/**
 * Elige la vía y decide si el PK sirve. Puro: sin red y sin React.
 *
 * `candidates` llega SIN recortar por umbral — es lo que permite el caso
 * `too-far` con su distancia.
 */
export function chooseRoadCandidate(
  candidates: PkCandidate[],
  preferredRoadId?: string | null,
  maxMeters: number = PK_MATCH_MAX_METERS
): PkResolution {
  if (candidates.length === 0) return { kind: 'no-data' };

  const ordenados = [...candidates].sort((a, b) => a.distance - b.distance);
  const dentro = ordenados.filter((c) => c.distance <= maxMeters);
  if (dentro.length === 0) return { kind: 'too-far', nearest: ordenados[0] };

  // La MISMA regla que usa el recorrido, no una copia.
  const elegido = pickRoadWithPreference(dentro, preferredRoadId) as PkCandidate;

  // 🔴 El `!roadRange ||` es la decisión, y va aquí a la vista y no dentro del
  // helper: `pkWithinRange` es FAIL-CLOSED —sin extremos devuelve `false`, que
  // es lo correcto cuando hay que ELEGIR entre varias vías del recorrido— pero
  // aquí sin cobertura no se conocen los extremos declarados de ninguna, y
  // rechazarlas todas dejaría el botón muerto justo donde tiene que servir. La
  // autoridad es el backend, igual que en `assertPksWithinRoad`.
  const cabe =
    !elegido.roadRange ||
    pkWithinRange(
      { km: elegido.pkKm, meter: elegido.pkMeter },
      elegido.roadRange
    );
  if (!cabe) return { kind: 'out-of-range', match: elegido };

  const otro = dentro.find((c) => c.roadId !== elegido.roadId);
  const ambiguo =
    otro && Math.abs(otro.distance - elegido.distance) < AMBIGUITY_MARGIN_METERS
      ? otro
      : undefined;

  return {
    kind: 'ok',
    match: elegido,
    changedRoad: !!preferredRoadId && elegido.roadId !== preferredRoadId,
    ...(ambiguo ? { ambiguousWith: ambiguo } : {}),
  };
}

/**
 * Tope de la consulta al backend.
 *
 * Mismo número y mismo criterio que `NETWORK_TIMEOUT_MS` de `useGpsToPK`: solo
 * se llega aquí cuando la malla no ha sabido, y más vale decirle al operario
 * «no sé» a los 3 s que dejarle mirando el botón mientras el driver de Mongo
 * agota sus 30 s.
 */
export const LOCATE_NETWORK_TIMEOUT_MS = 3_000;

/**
 * 🔴 LA MALLA PRIMERO, LA RED DESPUÉS.
 *
 * Es el orden que `useGpsToPK` adoptó el 2026-09-08 tras medirlo en carretera
 * (spec `docs/superpowers/specs/2026-09-08-pk-malla-primero-design.md`), y este
 * botón nació con el anterior. La malla vive en IndexedDB, es local,
 * instantánea y da la MISMA respuesta que el backend —misma proyección, gemela
 * de `pk-projection.helper.ts`—: preguntar a la red antes de mirarla no compra
 * nada.
 *
 * Y con cobertura degradada no era solo una espera. `navigator.onLine` dice
 * `true` con una raya, las queries no pasan por el techo de `OfflineQueueLink`
 * (ese es solo para mutations) y la petición ni contesta ni falla: el respaldo
 * de malla, escrito debajo del `catch`, era INALCANZABLE. El `finally` del hook
 * tampoco corría, así que el botón se quedaba deshabilitado para siempre.
 *
 * La red se conserva para cuando la malla no sabe, porque aporta algo que la
 * malla no tiene: el rango declarado de la vía (`roadRange`), que es lo que
 * permite el desenlace `out-of-range`.
 */
export async function resolvePkAtPosition(
  client: ApolloClient<unknown>,
  args: {
    lat: number;
    lng: number;
    preferredRoadId?: string | null;
    concessionId?: string | null;
  }
): Promise<PkResolution> {
  // ── 1 · La malla que la tablet lleva descargada desde el arranque ─────────
  const ownerKey = getSeedKey();
  if (ownerKey) await hydrateRoadMeshes(ownerKey);

  // Sin `roadIds`: el botón no sabe en qué vía está el operario, así que
  // compiten TODAS las guardadas.
  const entradas = getCachedRoadMeshEntries();
  // `entradas: 0` en campo significa que la cartografía NO está descargada, y
  // eso es un diagnóstico completamente distinto.
  traceAutolocate('source', {
    via: 'malla',
    entradas: entradas.length,
    vias: new Set(entradas.map((e) => e.roadId)).size,
  });

  const local = desdeLaMalla(args, entradas);
  if (local.kind === 'ok') return local;

  // ── 2 · La malla no sabe: la red, con tope ────────────────────────────────
  //
  // Un `too-far` de la malla NO es definitivo: puede ser un punto fuera de la
  // ventana cartografiada, y la red aún puede saber. Misma regla que el `null`
  // de la malla en `useGpsToPK`.
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return local;

  try {
    const { data } = await withTimeout(
      client.query<{ locatePk: LocatePkMatch[] }>({
        query: LOCATE_PK,
        fetchPolicy: 'network-only',
        variables: {
          input: {
            lat: args.lat,
            lng: args.lng,
            concessionId: args.concessionId ?? null,
          },
        },
      }),
      LOCATE_NETWORK_TIMEOUT_MS
    );
    const candidatos = (data?.locatePk ?? []).map(desdeBackend);
    traceAutolocate('source', { via: 'backend', candidatos: candidatos.length });
    if (candidatos.length > 0) {
      return chooseRoadCandidate(candidatos, args.preferredRoadId);
    }
  } catch (err) {
    // El tope agotado es lo ESPERADO con poca cobertura: no es un incidente y
    // llenaría Sentry de ruido en cada bache. Mismo criterio que `useGpsToPK`.
    if (!(err instanceof NetworkTimeoutError)) {
      Sentry.captureException(err, {
        tags: { feature: 'autolocate-pk', step: 'locatePk' },
        level: 'warning',
      });
    }
  }

  // La red no ha sabido: vale lo que dijo la malla, incluido su `too-far` con
  // la distancia — que es justo lo que el operario necesita leer.
  return local;
}

/** La resolución que sale de la cartografía guardada en la tablet. */
function desdeLaMalla(
  args: { lat: number; lng: number; preferredRoadId?: string | null },
  entradas: ReturnType<typeof getCachedRoadMeshEntries>
): PkResolution {
  const nombrePorVia = new Map(entradas.map((e) => [e.roadId, e.roadName]));
  // Umbral infinito porque recortar es de `chooseRoadCandidate`, que necesita
  // poder decir a qué distancia quedó la más cercana.
  const candidatos: PkCandidate[] = pkCandidatesFromMeshEntries(
    args.lat,
    args.lng,
    entradas,
    Number.POSITIVE_INFINITY
  ).map((c) => ({
    roadId: c.roadId,
    roadName: nombrePorVia.get(c.roadId) ?? '',
    direction: '',
    pkKm: c.pkKm,
    pkMeter: c.pkMeter,
    distance: c.distance,
    // Sin cobertura no se conocen los extremos declarados. Es fail-open a
    // propósito: la autoridad es el backend, y una tablet en un túnel tiene
    // que poder trabajar.
  }));

  return chooseRoadCandidate(candidatos, args.preferredRoadId);
}

/** Lo que devuelve `Query.locatePk`. */
type LocatePkMatch = {
  roadId: string;
  roadName: string;
  direction: string;
  pkKm: number;
  pkMeter: number;
  distance: number;
  roadPkInitKm: number;
  roadPkInitMeter: number;
  roadPkEndKm: number;
  roadPkEndMeter: number;
};

const desdeBackend = (m: LocatePkMatch): PkCandidate => ({
  roadId: m.roadId,
  roadName: m.roadName,
  direction: m.direction,
  pkKm: m.pkKm,
  pkMeter: m.pkMeter,
  distance: m.distance,
  roadRange: {
    pkInitKm: m.roadPkInitKm,
    pkInitMeter: m.roadPkInitMeter,
    pkEndKm: m.roadPkEndKm,
    pkEndMeter: m.roadPkEndMeter,
  },
});
